// src/pages/admin/FmSyncClient.tsx
//
// Management surface for the Rust FileMaker sync agent.
//
// This pane is deliberately SEPARATE from the FileMaker import/export
// pane (/admin/imports): the Rust client replaces that pane mid-term,
// but for now it stands alone and does not duplicate its controls.
//
// What it reads:
//   GET /api/admin/fm-sync/clients            -> the clients that reported
//   GET /api/admin/fm-sync/clients/{device_id} -> one client + its batches
//
// Everything on screen comes from those two endpoints. There is no
// seed store, no localStorage and no placeholder row: a client that
// has never reported simply is not listed, and a failed import is
// shown as a failure.
//
// The import REPORTS themselves live in the FileMaker import/export
// pane, where batches are listed and previewed — this pane links
// there instead of re-implementing the import controls.

import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Laptop,
  RefreshCcw,
  Loader2,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge, type BadgeVariant } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { authHeaders } from "@/lib/api";
import { useAuth } from "@/components/auth/AuthContext";

// ---------------------------------------------------------------------------
// API shapes
// ---------------------------------------------------------------------------

/** One sync client (a workstation running the Rust agent). */
interface FmSyncClient {
  device_id: string;
  tenant: string | null;
  environment: string | null;
  agent_version: string | null;
  last_seen_at: string | null;
  /** Absent on the detail endpoint, which folds no batch count in. */
  batch_count: number | undefined;
  last_batch_id: string | null;
  last_batch_status: string | null;
  last_control_totals: Record<string, string>;
}

interface FmSyncBatchFile {
  filename: string;
  kind: string;
  sha256: string;
  size_bytes: number | null;
  row_count: number | null;
}

interface FmSyncBatchTable {
  applied: number | null;
  status: string | null;
}

interface FmSyncBatch {
  batch_id: string;
  produced_at: string | null;
  manifest_hash: string | null;
  kind: string | null;
  status: string;
  staged_at: string | null;
  snapshot_id: string | null;
  files: FmSyncBatchFile[];
  control_totals: Record<string, string>;
  tables: Record<string, FmSyncBatchTable>;
}

interface FmSyncClientDetail {
  client: FmSyncClient;
  batches: FmSyncBatch[];
}

// ---------------------------------------------------------------------------
// Response parsing
//
// The API is the only source of truth here, so the payload is parsed
// defensively: a malformed row is dropped or shown as "—", never
// invented. A response that is not the documented shape is an error,
// not an empty list.
// ---------------------------------------------------------------------------

function isRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function str(v: unknown): string | null {
  if (typeof v === "string") return v;
  if (typeof v === "number" && Number.isFinite(v)) return String(v);
  return null;
}

function num(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Keep the control totals as the strings FileMaker produced them. */
function strMap(v: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!isRecord(v)) return out;
  for (const [k, val] of Object.entries(v)) {
    const s = str(val);
    if (s !== null) out[k] = s;
  }
  return out;
}

function parseClient(raw: unknown): FmSyncClient | null {
  if (!isRecord(raw)) return null;
  const deviceId = str(raw.device_id);
  // Without a device id the row cannot be addressed or selected.
  if (!deviceId) return null;
  return {
    device_id: deviceId,
    tenant: str(raw.tenant),
    environment: str(raw.environment),
    agent_version: str(raw.agent_version),
    last_seen_at: str(raw.last_seen_at),
    batch_count: num(raw.batch_count) ?? undefined,
    last_batch_id: str(raw.last_batch_id),
    last_batch_status: str(raw.last_batch_status),
    last_control_totals: strMap(raw.last_control_totals),
  };
}

function parseBatch(raw: unknown): FmSyncBatch | null {
  if (!isRecord(raw)) return null;
  const batchId = str(raw.batch_id);
  if (!batchId) return null;

  const tables: Record<string, FmSyncBatchTable> = {};
  if (isRecord(raw.tables)) {
    for (const [name, value] of Object.entries(raw.tables)) {
      const t = isRecord(value) ? value : {};
      tables[name] = { applied: num(t.applied), status: str(t.status) };
    }
  }

  const files: FmSyncBatchFile[] = [];
  if (Array.isArray(raw.files)) {
    for (const value of raw.files) {
      if (!isRecord(value)) continue;
      const filename = str(value.filename);
      if (!filename) continue;
      files.push({
        filename,
        kind: str(value.kind) ?? "",
        sha256: str(value.sha256) ?? "",
        size_bytes: num(value.size_bytes),
        row_count: num(value.row_count),
      });
    }
  }

  return {
    batch_id: batchId,
    produced_at: str(raw.produced_at),
    manifest_hash: str(raw.manifest_hash),
    kind: str(raw.kind),
    status: str(raw.status) ?? "unknown",
    staged_at: str(raw.staged_at),
    snapshot_id: str(raw.snapshot_id),
    files,
    control_totals: strMap(raw.control_totals),
    tables,
  };
}

/** GET a JSON endpoint, failing with the HTTP status when it does not. */
async function getJson(path: string): Promise<unknown> {
  const r = await fetch(path, { headers: authHeaders() });
  if (!r.ok) {
    let detail = `HTTP ${r.status}`;
    try {
      const body = (await r.json()) as { detail?: unknown };
      const d = str(body?.detail);
      if (d) detail = `HTTP ${r.status} — ${d}`;
    } catch {
      // Non-JSON error body: the status alone is the honest message.
    }
    throw new Error(detail);
  }
  return (await r.json()) as unknown;
}

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

/** The five statuses the API documents, plus a neutral fallback. */
function statusBadge(status: string | null): ReactNode {
  if (!status) return <span className="text-fg-quaternary">—</span>;
  const variant: BadgeVariant =
    status === "applied"
      ? "success"
      : status === "applied_partial"
        ? "warning"
        : status === "failed"
          ? "danger"
          : status === "pending"
            ? "info"
            : "default";
  return <Badge variant={variant} size="sm">{status}</Badge>;
}

/** ISO timestamp -> "2026-10-29 20:15:44Z" (UTC kept explicit). */
function fmtTs(iso: string | null): string {
  if (!iso) return "—";
  const base = iso.slice(0, 19).replace("T", " ");
  return iso.endsWith("Z") ? `${base}Z` : base;
}

function fmtBytes(n: number | null): string {
  if (n === null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtInt(n: number | null): string {
  // No thousands separator: in this pane a comma always means the
  // FileMaker decimal separator, so grouping counts would muddy it.
  return n === null ? "—" : String(n);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function FmSyncClientPage() {
  const auth = useAuth();
  const { t } = useTranslation();
  const [clients, setClients] = useState<FmSyncClient[]>([]);
  const [listState, setListState] = useState<"loading" | "ready" | "error">("loading");
  const [listError, setListError] = useState("");

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FmSyncClientDetail | null>(null);
  const [detailState, setDetailState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [detailError, setDetailError] = useState("");

  const loadClients = useCallback(async () => {
    setListState("loading");
    setListError("");
    try {
      const body = await getJson("/api/admin/fm-sync/clients");
      if (!isRecord(body) || !Array.isArray(body.clients)) {
        throw new Error("HTTP 200 — unexpected response shape");
      }
      const parsed = body.clients
        .map((raw) => parseClient(raw))
        .filter((c): c is FmSyncClient => c !== null);
      setClients(parsed);
      setListState("ready");
    } catch (e) {
      setClients([]);
      setListError(e instanceof Error ? e.message : String(e));
      setListState("error");
    }
  }, []);

  const loadClient = useCallback(async (deviceId: string) => {
    setSelectedId(deviceId);
    setDetailState("loading");
    setDetailError("");
    setDetail(null);
    try {
      const body = await getJson(
        `/api/admin/fm-sync/clients/${encodeURIComponent(deviceId)}`,
      );
      if (!isRecord(body)) {
        throw new Error("HTTP 200 — unexpected response shape");
      }
      const client = parseClient(body.client);
      if (!client) {
        throw new Error("HTTP 200 — unexpected response shape");
      }
      const batches = Array.isArray(body.batches)
        ? body.batches
            .map((raw) => parseBatch(raw))
            .filter((b): b is FmSyncBatch => b !== null)
        : [];
      setDetail({ client, batches });
      setDetailState("ready");
    } catch (e) {
      setDetailError(e instanceof Error ? e.message : String(e));
      setDetailState("error");
    }
  }, []);

  useEffect(() => {
    if (auth.isReady && auth.isAuthenticated) {
      void loadClients();
    }
  }, [auth.isReady, auth.isAuthenticated, loadClients]);

  // Show the first client's batches straight away: for a one-Mac shop
  // that is the only client, and an empty panel would hide the data.
  useEffect(() => {
    if (listState !== "ready" || selectedId !== null) return;
    const first = clients[0];
    if (first) void loadClient(first.device_id);
  }, [listState, clients, selectedId, loadClient]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Laptop className="h-5 w-5" /> {t("admin.fmSyncClient.title")}
          </CardTitle>
          <CardDescription>
            The Rust agent runs on the FileMaker workstation and uploads
            one immutable batch per export. This pane lists the clients
            that reported and the batches they produced. The import
            reports themselves live in the FileMaker import/export pane,
            where batches are listed and previewed —{" "}
            <Link
              to="/admin/imports"
              className="underline underline-offset-2 hover:text-benchmark-teal-bright"
            >
              FM imports
            </Link>
            .
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                void loadClients();
                // Keep the open panel in step with the refreshed list.
                if (selectedId) void loadClient(selectedId);
              }}
            >
              <RefreshCcw className="h-4 w-4" /> Refresh
            </Button>
            {listState === "loading" && (
              <span role="status" className="flex items-center gap-2 text-xs text-fg-tertiary">
                <Loader2 className="h-4 w-4 animate-spin" /> {t("admin.fmSyncClient.loadingClients")}
              </span>
            )}
          </div>
          {listState === "error" && (
            <div
              role="alert"
              className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose"
            >
              Could not load the sync clients — {listError}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Clients
            <span className="ml-auto text-xs text-fg-tertiary">
              {listState === "ready" ? `${clients.length} reporting` : ""}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {listState === "error" ? (
            <p className="text-sm text-fg-tertiary">
              No client list available: the request failed.
            </p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-fg-tertiary">
                  <th className="py-1">{t("admin.fmSyncClient.deviceColumn")}</th>
                  <th>{t("admin.fmSyncClient.tenantEnv")}</th>
                  <th>{t("admin.fmSyncClient.agentColumn")}</th>
                  <th>{t("admin.fmSyncClient.lastSeen")}</th>
                  <th>{t("admin.fmSyncClient.batchesColumn")}</th>
                  <th>{t("admin.fmSyncClient.lastBatch")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr
                    key={c.device_id}
                    className={
                      selectedId === c.device_id
                        ? "border-b border-border/50 bg-bg-elevated/40"
                        : "border-b border-border/50"
                    }
                  >
                    <td className="py-1 font-mono text-xs">{c.device_id}</td>
                    <td className="text-xs">
                      {c.tenant ?? "—"} / {c.environment ?? "—"}
                    </td>
                    <td className="text-xs">{c.agent_version ?? "—"}</td>
                    <td className="text-xs">{fmtTs(c.last_seen_at)}</td>
                    <td className="text-xs">{fmtInt(c.batch_count ?? null)}</td>
                    <td className="text-xs">{statusBadge(c.last_batch_status)}</td>
                    <td className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void loadClient(c.device_id)}
                      >
                        <FileSpreadsheet className="h-3.5 w-3.5" />{t("admin.fmSyncClient.batchesColumn")}
                      </Button>
                    </td>
                  </tr>
                ))}
                {listState === "ready" && clients.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-4 text-center text-fg-tertiary">
                      No sync client has reported yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {detailState === "error" && (
        <Card>
          <CardContent>
            <div
              role="alert"
              className="rounded-md border border-accent-rose/30 bg-accent-rose/10 p-3 text-sm text-accent-rose"
            >
              Could not load the batches of {selectedId} — {detailError}
            </div>
          </CardContent>
        </Card>
      )}

      {detailState === "loading" && (
        <Card>
          <CardContent>
            <span role="status" className="flex items-center gap-2 text-sm text-fg-tertiary">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("admin.fmSyncClient.loadingBatches")}
            </span>
          </CardContent>
        </Card>
      )}

      {detailState === "ready" && detail && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {detail.client.device_id}
                <span className="text-xs font-normal text-fg-tertiary">
                  {detail.client.tenant ?? "—"} / {detail.client.environment ?? "—"} · agent{" "}
                  {detail.client.agent_version ?? "—"} · last seen{" "}
                  {fmtTs(detail.client.last_seen_at)}
                </span>
              </CardTitle>
              <CardDescription>
                {t("admin.fmSyncClient.lastBatch")}: {detail.client.last_batch_id ?? "—"} ·{" "}
                {detail.client.last_batch_status ?? "—"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <h5 className="text-xs uppercase tracking-wider text-fg-tertiary">{t("admin.fmSyncClient.controlTotalsTitle")}
              </h5>
              <ControlTotals totals={detail.client.last_control_totals} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">{t("admin.fmSyncClient.batchesColumn")}
                <span className="ml-auto text-xs text-fg-tertiary">
                  {detail.batches.length} uploaded
                </span>
              </CardTitle>
              <CardDescription>
                Newest first. A batch is only "applied" once the import
                pipeline has run it; "staged" means the files are on disk
                but not imported yet.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {detail.batches.length === 0 ? (
                <p className="text-sm text-fg-tertiary">{t("admin.fmSyncClient.noBatchYet")}
                </p>
              ) : (
                detail.batches.map((b) => <BatchCard key={b.batch_id} batch={b} />)
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function ControlTotals({ totals }: { totals: Record<string, string> }) {
  const { t } = useTranslation();
  const entries = Object.entries(totals);
  if (entries.length === 0) {
    return <p className="text-xs text-fg-quaternary">{t("admin.fmSyncClient.noControlTotals")}</p>;
  }
  return (
    <table className="w-full text-left text-xs">
      <thead>
        <tr className="border-b border-border text-fg-tertiary">
          <th className="py-1">{t("admin.fmSyncClient.measure")}</th>
          <th>{t("admin.fmSyncClient.valueHeader")}</th>
        </tr>
      </thead>
      <tbody>
        {entries.map(([name, value]) => (
          <tr key={name} className="border-b border-border/50">
            <td className="py-1 font-mono">{name}</td>
            <td className="font-mono">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BatchCard({ batch }: { batch: FmSyncBatch }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const tableEntries = Object.entries(batch.tables);
  return (
    <div className="rounded-md border border-border bg-bg-canvas/30">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-fg-quaternary" strokeWidth={1.5} />
        ) : (
          <ChevronRight className="h-4 w-4 text-fg-quaternary" strokeWidth={1.5} />
        )}
        <code className="font-mono text-xs">{batch.batch_id}</code>
        {statusBadge(batch.status)}
        <span className="text-xs text-fg-tertiary">{batch.kind ?? "—"}</span>
        <span className="ml-auto text-xs text-fg-tertiary">
          {fmtTs(batch.produced_at)}
        </span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-border px-3 py-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="text-xs text-fg-tertiary">
              <div>manifest {batch.manifest_hash ?? "—"}</div>
              <div>staged {fmtTs(batch.staged_at)}</div>
              <div>snapshot {batch.snapshot_id ?? "—"}</div>
            </div>
            <div>
              <h5 className="mb-1 text-xs uppercase tracking-wider text-fg-tertiary">
                Tables applied
              </h5>
              {tableEntries.length === 0 ? (
                <p className="text-xs text-fg-quaternary">
                  No per-table report for this batch.
                </p>
              ) : (
                <table className="w-full text-left text-xs">
                  <tbody>
                    {tableEntries.map(([name, t]) => (
                      <tr key={name} className="border-b border-border/40">
                        <td className="py-0.5 font-mono">{name}</td>
                        <td className="text-right">{fmtInt(t.applied)}</td>
                        <td className="w-20 text-right">
                          {t.status ? (
                            <Badge variant={t.status === "ok" ? "success" : "default"} size="sm">
                              {t.status}
                            </Badge>
                          ) : (
                            <span className="text-fg-quaternary">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div>
            <h5 className="mb-1 text-xs uppercase tracking-wider text-fg-tertiary">{t("admin.fmSyncClient.controlTotalsHeading")}
            </h5>
            <ControlTotals totals={batch.control_totals} />
          </div>

          {batch.files.length > 0 && (
            <div>
              <h5 className="mb-1 text-xs uppercase tracking-wider text-fg-tertiary">
                Files
              </h5>
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-border text-fg-quaternary">
                    <th className="py-1">{t("admin.fmSyncClient.fileColumn")}</th>
                    <th>{t("admin.fmSyncClient.kindColumn")}</th>
                    <th className="text-right">{t("admin.fmSyncClient.rowsColumn")}</th>
                    <th className="text-right">{t("admin.fmSyncClient.sizeColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {batch.files.map((f) => (
                    <tr key={f.filename} className="border-b border-border/40">
                      <td className="py-0.5 font-mono">{f.filename}</td>
                      <td className="text-fg-tertiary">{f.kind || "—"}</td>
                      <td className="text-right">{fmtInt(f.row_count)}</td>
                      <td className="text-right">{fmtBytes(f.size_bytes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
